import { z } from 'zod'

import { TeacherIdSchema } from '~/@entities/Teacher'
import { WorkbookSecuringTypeSchema } from '~/@entities/Workbook'
import { WorksheetFilterTypeSchema } from '~/@entities/Worksheet'

// 선생님 설정은 Teacher 와 따로 둔다. 학습지 필터 종류를 Worksheet 에서 가져오는데 Worksheet 가 Teacher 의 id 를
// 참조하므로, Teacher 안에 두면 엔티티 순환이 생긴다.

/***************** 난이도 단계 *****************/
// 학습지 만들기의 난이도 단계. 단계마다 문제 난이도 1~5 의 출제 비율을 갖는다
export const DIFFICULTY_LEVEL = ['veryLow', 'low', 'middle', 'high', 'veryHigh'] as const

export const DifficultyLevelSchema = z.enum(DIFFICULTY_LEVEL)
export type DifficultyLevel = z.infer<typeof DifficultyLevelSchema>

// 서버가 저장할 때 다섯 칸·합 100·음수 없음을 검증한다(DifficultySettingValidator)
const DifficultyRatesSchema = z.tuple([z.number(), z.number(), z.number(), z.number(), z.number()])

const DifficultySettingSchema = z.object({ difficultySetting: DifficultyRatesSchema })

export const TeacherWorksheetSearchSettingSchema = z.object({
  filterTypes: z.array(WorksheetFilterTypeSchema).nullable(),
  createStartDate: z.iso.date().nullable(),
  createEndDate: z.iso.date().nullable(),
  teacherIds: z.array(TeacherIdSchema).nullable(),
})
export type TeacherWorksheetSearchSetting = z.infer<typeof TeacherWorksheetSearchSettingSchema>

// 앱은 publicWorkbookSecuringType 을 읽지 않지만, 받은 설정을 펼쳐 다시 저장하는 화면이 있어 받아 둔다.
// 버리면 그 저장이 서버 값을 null 로 덮는다.
export const TeacherWorkbookSearchSettingSchema = z.object({
  publicWorkbookSecuringType: WorkbookSecuringTypeSchema.nullable(),
  publicWorkbookAnswerConfirmed: z.boolean().nullable(),
})
export type TeacherWorkbookSearchSetting = z.infer<typeof TeacherWorkbookSearchSettingSchema>

export const TeacherQuickMenuSettingSchema = z.object({
  quickMenuIds: z.array(z.number()),
})
export type TeacherQuickMenuSetting = z.infer<typeof TeacherQuickMenuSettingSchema>

/** 연구소 설정. 학생 성취도 리포트에 AI 를 쓸지 고른다 */
export const TeacherLabSettingSchema = z.object({
  studentAchievementReportFlag: z.boolean().nullable(),
})
export type TeacherLabSetting = z.infer<typeof TeacherLabSettingSchema>

// /teachers/setting 응답. 검색·연구소 설정은 서버가 늘 객체로 만들어 싣고(안의 값이 null), 빠른 메뉴는 저장한 적이
// 없으면 null 이다. 2026-10-14 백엔드 develop(6a0f8e7bb) 의 teacher/model/dto/TeacherSettingDto.kt·
// common/communicator/response/AuthServerMemberTeacherSettingResponse.kt 와 팀이 녹음한 dev 응답 e2e 목
// (teachers-setting.ts)으로 확인.
export const TeacherSettingSchema = z.object({
  veryLow: DifficultySettingSchema,
  low: DifficultySettingSchema,
  middle: DifficultySettingSchema,
  high: DifficultySettingSchema,
  veryHigh: DifficultySettingSchema,
  worksheetSearchSetting: TeacherWorksheetSearchSettingSchema.nullable(),
  workbookSearchSetting: TeacherWorkbookSearchSettingSchema.nullable(),
  quickMenuSetting: TeacherQuickMenuSettingSchema.nullable(),
  labSetting: TeacherLabSettingSchema.nullable(),
})
export type TeacherSetting = z.infer<typeof TeacherSettingSchema>
